// src/lib/api_whatsapp.ts
// Cliente para el módulo WhatsApp del backend Django (Track B — B1→B4).
// Mirrors apps/whatsapp/schemas.py — /admin/whatsapp/{templates,contacts,broadcasts}.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const url = `${DJANGO_PROXY}${path}`;
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 409) throw new ApiError(detail || 'Conflicto.', 409);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación.', 422);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ═══════════════════════════════════════════════════════════════
// Pagination wrapper
// ═══════════════════════════════════════════════════════════════

export interface Paginated<T> { items: T[]; count: number }

// ═══════════════════════════════════════════════════════════════
// Templates (B2)
// ═══════════════════════════════════════════════════════════════

export type TemplateStatus =
  | 'draft' | 'pending' | 'approved' | 'rejected' | 'paused' | 'disabled';

export type TemplateCategory = 'AUTHENTICATION' | 'UTILITY' | 'MARKETING';

export type HeaderFormat = '' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';

export interface WhatsAppTemplate {
  id: number;
  org_id: string;
  name: string;
  category: TemplateCategory;
  language: string;
  header_format: HeaderFormat;
  header_text: string;
  body: string;
  footer: string;
  buttons: Array<Record<string, unknown>>;
  variables_example: string[];
  status: TemplateStatus;
  meta_template_id: string;
  rejection_reason: string;
  error_message: string;
  submitted_at: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TemplateIn {
  name: string;
  body: string;
  category?: TemplateCategory;
  language?: string;
  header_format?: HeaderFormat;
  header_text?: string;
  footer?: string;
  buttons?: Array<Record<string, unknown>>;
  variables_example?: string[];
}

export interface TemplateActionOut {
  template: WhatsAppTemplate;
  status: 'queued' | 'synced' | 'skipped' | 'error';
  task_id?: string | null;
  detail?: string;
}

export async function listTemplates(
  filters: {
    status?: TemplateStatus; category?: TemplateCategory;
    language?: string; q?: string; limit?: number; offset?: number;
  } = {},
): Promise<Paginated<WhatsAppTemplate>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<WhatsAppTemplate>>(
    `/api/v1/admin/whatsapp/templates${qs({ ...rest, limit, offset })}`,
  );
}

export async function getTemplate(id: number): Promise<WhatsAppTemplate> {
  return djangoFetch<WhatsAppTemplate>(`/api/v1/admin/whatsapp/templates/${id}`);
}

export async function createTemplate(data: TemplateIn): Promise<WhatsAppTemplate> {
  return djangoFetch<WhatsAppTemplate>('/api/v1/admin/whatsapp/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTemplate(
  id: number, data: Partial<TemplateIn>,
): Promise<WhatsAppTemplate> {
  return djangoFetch<WhatsAppTemplate>(`/api/v1/admin/whatsapp/templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteTemplate(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/whatsapp/templates/${id}`, {
    method: 'DELETE',
  });
}

export async function submitTemplate(id: number): Promise<TemplateActionOut> {
  return djangoFetch<TemplateActionOut>(
    `/api/v1/admin/whatsapp/templates/${id}/submit`,
    { method: 'POST', body: '{}' },
  );
}

export async function syncTemplate(id: number): Promise<TemplateActionOut> {
  return djangoFetch<TemplateActionOut>(
    `/api/v1/admin/whatsapp/templates/${id}/sync`,
    { method: 'POST', body: '{}' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Contacts (B3)
// ═══════════════════════════════════════════════════════════════

export interface WhatsAppContact {
  id: number;
  org_id: string;
  phone_number: string;
  display_name: string;
  opted_in: boolean;
  opt_in_source: string;
  opt_in_at: string | null;
  opted_out_at: string | null;
  tags: string[];
  context_data: Record<string, unknown>;
  is_blocked: boolean;
  first_seen_at: string | null;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactIn {
  phone_number: string;
  display_name?: string;
  opted_in?: boolean;
  opt_in_source?: string;
  tags?: string[];
  context_data?: Record<string, unknown>;
}

export interface ContactUpdateIn {
  display_name?: string;
  opted_in?: boolean;
  opt_in_source?: string;
  tags?: string[];
  context_data?: Record<string, unknown>;
  is_blocked?: boolean;
}

export async function listContacts(
  filters: {
    opted_in?: boolean; is_blocked?: boolean; tag?: string; q?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<WhatsAppContact>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<WhatsAppContact>>(
    `/api/v1/admin/whatsapp/contacts${qs({ ...rest, limit, offset })}`,
  );
}

export async function getContact(id: number): Promise<WhatsAppContact> {
  return djangoFetch<WhatsAppContact>(`/api/v1/admin/whatsapp/contacts/${id}`);
}

export async function createContact(data: ContactIn): Promise<WhatsAppContact> {
  return djangoFetch<WhatsAppContact>('/api/v1/admin/whatsapp/contacts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateContact(id: number, data: ContactUpdateIn): Promise<WhatsAppContact> {
  return djangoFetch<WhatsAppContact>(`/api/v1/admin/whatsapp/contacts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteContact(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/whatsapp/contacts/${id}`, {
    method: 'DELETE',
  });
}

// ── Per-contact send ─────────────────────────────────────────

export type MessageDirection = 'inbound' | 'outbound';
export type MessageType =
  | 'text' | 'template' | 'image' | 'video' | 'audio' | 'document'
  | 'location' | 'sticker' | 'contacts' | 'button' | 'interactive'
  | 'reaction' | 'system';
export type WaStatus = 'queued' | 'sent' | 'delivered' | 'read' | 'failed';

export interface WhatsAppMessage {
  id: number;
  org_id: string;
  contact_id: number;
  direction: MessageDirection;
  message_type: MessageType;
  wa_status: WaStatus;
  meta_message_id: string;
  body: string;
  media_url: string;
  media_mime_type: string;
  template_id: number | null;
  broadcast_id: number | null;
  error_message: string;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  created_at: string;
}

export async function sendContactText(id: number, body: string): Promise<WhatsAppMessage> {
  return djangoFetch<WhatsAppMessage>(
    `/api/v1/admin/whatsapp/contacts/${id}/send-text`,
    { method: 'POST', body: JSON.stringify({ body }) },
  );
}

export async function sendContactTemplate(
  id: number, template_id: number, variables: string[] = [],
): Promise<WhatsAppMessage> {
  return djangoFetch<WhatsAppMessage>(
    `/api/v1/admin/whatsapp/contacts/${id}/send-template`,
    { method: 'POST', body: JSON.stringify({ template_id, variables }) },
  );
}

export async function listContactMessages(
  id: number, params: { limit?: number; offset?: number } = {},
): Promise<Paginated<WhatsAppMessage>> {
  const { limit = 50, offset = 0 } = params;
  return djangoFetch<Paginated<WhatsAppMessage>>(
    `/api/v1/admin/whatsapp/contacts/${id}/messages${qs({ limit, offset })}`,
  );
}

// ═══════════════════════════════════════════════════════════════
// Broadcasts (B3)
// ═══════════════════════════════════════════════════════════════

export type BroadcastStatus =
  | 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface WhatsAppBroadcast {
  id: number;
  org_id: string;
  name: string;
  template_id: number;
  audience: Record<string, unknown>;
  variables_default: Record<string, unknown>;
  status: BroadcastStatus;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  error_message: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BroadcastIn {
  name: string;
  template_id: number;
  audience?: Record<string, unknown>;
  variables_default?: Record<string, unknown>;
}

export interface BroadcastUpdateIn {
  name?: string;
  audience?: Record<string, unknown>;
  variables_default?: Record<string, unknown>;
}

export interface BroadcastActionOut {
  broadcast: WhatsAppBroadcast;
  status: 'queued' | 'cancelled' | 'skipped' | 'error';
  task_id?: string | null;
  detail?: string;
}

export interface AudiencePreview {
  count: number;
  sample: WhatsAppContact[];
}

export async function listBroadcasts(
  filters: {
    status?: BroadcastStatus; template_id?: number;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<WhatsAppBroadcast>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<WhatsAppBroadcast>>(
    `/api/v1/admin/whatsapp/broadcasts${qs({ ...rest, limit, offset })}`,
  );
}

export async function getBroadcast(id: number): Promise<WhatsAppBroadcast> {
  return djangoFetch<WhatsAppBroadcast>(`/api/v1/admin/whatsapp/broadcasts/${id}`);
}

export async function createBroadcast(data: BroadcastIn): Promise<WhatsAppBroadcast> {
  return djangoFetch<WhatsAppBroadcast>('/api/v1/admin/whatsapp/broadcasts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBroadcast(id: number, data: BroadcastUpdateIn): Promise<WhatsAppBroadcast> {
  return djangoFetch<WhatsAppBroadcast>(`/api/v1/admin/whatsapp/broadcasts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteBroadcast(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/whatsapp/broadcasts/${id}`, {
    method: 'DELETE',
  });
}

export async function previewBroadcastAudience(id: number): Promise<AudiencePreview> {
  return djangoFetch<AudiencePreview>(
    `/api/v1/admin/whatsapp/broadcasts/${id}/audience`,
  );
}

export async function startBroadcast(id: number): Promise<BroadcastActionOut> {
  return djangoFetch<BroadcastActionOut>(
    `/api/v1/admin/whatsapp/broadcasts/${id}/start`,
    { method: 'POST', body: '{}' },
  );
}

export async function cancelBroadcast(id: number): Promise<BroadcastActionOut> {
  return djangoFetch<BroadcastActionOut>(
    `/api/v1/admin/whatsapp/broadcasts/${id}/cancel`,
    { method: 'POST', body: '{}' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Helpers (frontend convenience)
// ═══════════════════════════════════════════════════════════════

/** Parse {{1}}, {{2}}, … out of template body in insertion order. */
export function extractTemplateVariables(body: string): string[] {
  const matches = body.match(/\{\{(\d+)\}\}/g) || [];
  const unique = Array.from(new Set(matches));
  unique.sort((a, b) => Number(a.slice(2, -2)) - Number(b.slice(2, -2)));
  return unique;
}

/** Human progress string for a broadcast row. */
export function broadcastProgressText(bc: WhatsAppBroadcast): string {
  const total = bc.total_recipients || 0;
  if (!total) return '—';
  const sent = bc.sent_count;
  const failed = bc.failed_count;
  return `${sent}/${total}${failed ? ` · ✗ ${failed}` : ''}`;
}
